import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck,
  faEnvelope,
  faSlidersSimple,
} from '@rq-ratings/pro-solid-svg-icons'
import { useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import { Badge, Button, Modal } from 'react-bootstrap'

import useCurrentCompanyUserOrFail from '../../../../hooks/useCurrentCompanyUserOrFail'
import { QUERY_KEYS } from '../../../../lib/queryKeys'
import { INTEGRATION_SERVICE } from '../../../../types/misc'
import { NylasIntegrationItem } from '../../../../types/responses/company-integrations'
import { NylasEmailIntegrationItem } from '../../../../types/responses/company-user-integrations'
import ConnectedAccountAlert from './ConnectedAccountAlert'
import ConnectUserAccountButton from './ConnectUserAccountButton'
import DeleteIntegrationButton from './DeleteIntegrationButton'
import DisconnectCompanyEmailButton from './DisconnectCompanyEmailButton'

interface Props {
  nylasEmailCompanyUserIntegration?: NylasEmailIntegrationItem | null
  companyNylasIntegration?: NylasIntegrationItem
  isDisconnectInProgress: boolean
  onUpdateDisconnectInProgress: (value: boolean) => void
}

const PERSONAL_EMAIL_TOOLTIP =
  "This is how your personal comms will be sent. To send via your company's shared email instead, disconnect this email account."

const ConnectEmailAccountsModal: React.FC<Props> = ({
  nylasEmailCompanyUserIntegration,
  companyNylasIntegration,
  isDisconnectInProgress,
  onUpdateDisconnectInProgress,
}) => {
  const currentCompanyUser = useCurrentCompanyUserOrFail()
  const queryClient = useQueryClient()

  const [show, setShow] = useState<boolean>(false)

  const companyHasEmailAuth =
    companyNylasIntegration?.enabled &&
    companyNylasIntegration?.secureConfigValid &&
    !!companyNylasIntegration?.config.companyEmail?.grantId

  const companyUserHasEmailAuth =
    !!nylasEmailCompanyUserIntegration &&
    nylasEmailCompanyUserIntegration.enabled &&
    nylasEmailCompanyUserIntegration.secureConfigValid

  return (
    <>
      <Button onClick={() => setShow(true)} size="lg">
        <FontAwesomeIcon icon={faSlidersSimple} className="me-2" />
        Configure
      </Button>

      <Modal
        show={show}
        centered
        fullscreen="md-down"
        onHide={() => setShow(false)}
      >
        <Modal.Header closeButton className="border-0">
          <h2 className="m-0">
            <strong>Connect email</strong>
          </h2>
        </Modal.Header>

        <Modal.Body>
          <div className="d-flex flex-column gap-4">
            <div className="card d-flex flex-column p-4 border m-0">
              <h3 className="d-flex gap-3 align-items-start justify-content-between">
                Personal email account
                {companyUserHasEmailAuth && (
                  <Badge pill bg="success" className="fs-5">
                    <FontAwesomeIcon icon={faCheck} className="me-2" />
                    Connected
                  </Badge>
                )}
              </h3>

              <p>
                Connect your personal email address. All of your RQ comms will
                be sent via this address.
              </p>

              {!companyUserHasEmailAuth && (
                <ConnectUserAccountButton
                  icon={faEnvelope}
                  text="Connect your email"
                  integratedService={INTEGRATION_SERVICE.nylasEmail}
                />
              )}

              {companyUserHasEmailAuth && (
                <div className="d-flex flex-column align-items-start gap-3">
                  <ConnectedAccountAlert
                    isCurrent={companyHasEmailAuth}
                    tooltipText={PERSONAL_EMAIL_TOOLTIP}
                  >
                    {nylasEmailCompanyUserIntegration.config.email} is connected
                  </ConnectedAccountAlert>

                  <DeleteIntegrationButton
                    companyUserIntegrationId={
                      nylasEmailCompanyUserIntegration.id
                    }
                    // We don't want two integrations being deleted at the same time
                    disabled={isDisconnectInProgress}
                    integratedService={INTEGRATION_SERVICE.nylasEmail}
                    onMutationStarted={() => onUpdateDisconnectInProgress(true)}
                    onError={() => onUpdateDisconnectInProgress(false)}
                    onDeleted={() => {
                      onUpdateDisconnectInProgress(false)

                      queryClient.invalidateQueries({
                        queryKey: QUERY_KEYS.companyUserIntegrationByType(
                          INTEGRATION_SERVICE.nylasEmail,
                        ),
                      })

                      queryClient.invalidateQueries({
                        queryKey:
                          QUERY_KEYS.companyCompanyUserIntegrationsByType(
                            INTEGRATION_SERVICE.nylasEmail,
                          ),
                      })
                    }}
                  />
                </div>
              )}
            </div>

            <div className="card d-flex flex-column p-4 border m-0">
              <h3 className="d-flex gap-3 align-items-start justify-content-between">
                Shared email account
                {companyHasEmailAuth && (
                  <Badge pill bg="success" className="fs-5">
                    <FontAwesomeIcon icon={faCheck} className="me-2" />
                    Connected
                  </Badge>
                )}
              </h3>

              <p>
                Connect a shared email account for all of your company's comms.
                This won't apply for any users in your company who connect their
                own email accounts above.
              </p>

              {!companyHasEmailAuth && (
                <ConnectUserAccountButton
                  icon={faEnvelope}
                  text="Connect shared email"
                  integratedService={INTEGRATION_SERVICE.nylas}
                  disabled={!currentCompanyUser.isCompanyAdmin}
                />
              )}

              {companyHasEmailAuth && (
                <div className="d-flex flex-column align-items-start gap-3">
                  <ConnectedAccountAlert>
                    {companyNylasIntegration.config.companyEmail?.email} is
                    connected
                  </ConnectedAccountAlert>

                  <div>
                    <DisconnectCompanyEmailButton
                      // We don't want two integrations being deleted/disconnected at the same time
                      disabled={isDisconnectInProgress}
                      onMutationStarted={() =>
                        onUpdateDisconnectInProgress(true)
                      }
                      onError={() => onUpdateDisconnectInProgress(false)}
                      onDeleted={() => {
                        onUpdateDisconnectInProgress(false)

                        queryClient.invalidateQueries({
                          queryKey: QUERY_KEYS.companyNylasIntegration,
                        })
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  )
}

export default ConnectEmailAccountsModal
